"use client";

import { c as _c } from "react/compiler-runtime";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import Link from "next/link";
import { memo, useCallback, useState } from "react";
import type { DropInteractionParams } from "../drop.types";
import { DropLocation } from "../drop.types";
import { getRankHoverBorderClass, getRankStaticBorderClass } from "../dropRankStyles";
import WaveDropActions from "../WaveDropActions";
import WaveDropAuthorPfp from "../WaveDropAuthorPfp";
import WaveDropContent from "../WaveDropContent";
import WaveDropHeader from "../WaveDropHeader";
import WaveDropMetadata from "../WaveDropMetadata";
import WaveDropMobileMenu from "../WaveDropMobileMenu";
import WaveDropRatings from "../WaveDropRatings";
import WaveDropReactions from "../WaveDropReactions";
import WaveDropReply from "../WaveDropReply";
import WinnerDropBadge from "./WinnerDropBadge";
import { WaveWinnerIdentity } from "@/components/waves/winners/identity/WaveWinnerIdentity";
import { getWinnerVisibleMetadata } from "@/components/waves/winners/identity/winnerIdentity.helpers";
const getRankHoverClass = (rank: number | null): string => {
  return getRankHoverBorderClass(rank);
};
const getDropStyles = (isActiveDrop: boolean) => {
  if (isActiveDrop) {
    return {
      borderColor: "rgba(60,203,127,0.45)",
      boxShadow: "inset 0 0 0 1px rgba(60,203,127,0.2), inset 0 0 24px rgba(60,203,127,0.08)"
    };
  }
  return undefined;
};
interface DefautWinnerDropProps {
  readonly drop: ExtendedDrop;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly dropViewDropId: string | null;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onReplyClick: (serialNo: number) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
}
const DefaultWinnerDrop = t0 => {
  const $ = _c(90);
  const {
    drop,
    showWaveInfo,
    activeDrop,
    location,
    dropViewDropId,
    onReply,
    onReplyClick,
    onQuoteClick,
    onDropContentClick,
    showReplyAndQuote
  } = t0;
  const [activePartIndex, setActivePartIndex] = useState(0);
  const [isSlideUp, setIsSlideUp] = useState(false);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const isStorm = drop.parts.length > 1;
  const isMobile = useIsMobileDevice();
  const hasTouch = useIsTouchDevice() || isMobile;
  const effectiveRank = drop.winning_context?.place ?? drop.rank;
  const decisionTime = drop.winning_context?.decision_time;
  let t1;
  if ($[0] !== drop.metadata || $[1] !== drop.wave) {
    t1 = getWinnerVisibleMetadata({
      wave: drop.wave,
      metadata: drop.metadata
    });
    $[0] = drop.metadata;
    $[1] = drop.wave;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const visibleMetadata = t1;
  const getBackgroundColorClass = _temp;
  const bgColorClass = isActiveDrop ? "tw-bg-[#3CCB7F]/10" : getBackgroundColorClass(location);
  let t2;
  if ($[3] !== hasTouch) {
    t2 = () => {
      if (!hasTouch) {
        return;
      }
      setLongPressTriggered(true);
      setIsSlideUp(true);
    };
    $[3] = hasTouch;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleLongPress = t2;
  let t3;
  if ($[5] !== activePartIndex || $[6] !== drop || $[7] !== onReply) {
    t3 = () => {
      setIsSlideUp(false);
      onReply({
        drop,
        partId: drop.parts[activePartIndex]?.part_id
      });
    };
    $[5] = activePartIndex;
    $[6] = drop;
    $[7] = onReply;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleOnReply = t3;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setIsSlideUp(false);
    };
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleOnAddReaction = t4;
  const t5 = `tw-w-full ${location === DropLocation.WAVE ? "tw-px-4 tw-py-1" : ""}`;
  let t6;
  if ($[10] !== effectiveRank) {
    t6 = getRankStaticBorderClass(effectiveRank);
    $[10] = effectiveRank;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== effectiveRank) {
    t7 = getRankHoverClass(effectiveRank);
    $[12] = effectiveRank;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = `tw-group tw-relative tw-flex tw-w-full tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid ${t6} tw-px-4 tw-py-3 ${bgColorClass} ${t7}`;
  let t9;
  if ($[14] !== isActiveDrop) {
    t9 = getDropStyles(isActiveDrop);
    $[14] = isActiveDrop;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t9) {
    t10 = {
      ...t9,
      transition: "box-shadow 0.2s ease, background-color 0.2s ease"
    };
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== drop.reply_to || $[19] !== drop.wave || $[20] !== dropViewDropId || $[21] !== onReplyClick) {
    t11 = drop.reply_to && drop.reply_to.drop_id !== dropViewDropId && <WaveDropReply onReplyClick={onReplyClick} dropId={drop.reply_to.drop_id} dropPartId={drop.reply_to.drop_part_id} maybeDrop={drop.reply_to.drop ? {
      ...drop.reply_to.drop,
      wave: drop.wave
    } : null} />;
    $[18] = drop.reply_to;
    $[19] = drop.wave;
    $[20] = dropViewDropId;
    $[21] = onReplyClick;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== drop) {
    t12 = <WaveDropAuthorPfp drop={drop} />;
    $[23] = drop;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const t13 = decisionTime ?? null;
  let t14;
  if ($[25] !== effectiveRank || $[26] !== t13) {
    t14 = <WinnerDropBadge rank={effectiveRank} decisionTime={t13} />;
    $[25] = effectiveRank;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== activePartIndex || $[29] !== drop || $[30] !== isStorm || $[31] !== t14) {
    t15 = <WaveDropHeader drop={drop} showWaveInfo={false} isStorm={isStorm} currentPartIndex={activePartIndex} partsCount={drop.parts.length} badge={t14} />;
    $[28] = activePartIndex;
    $[29] = drop;
    $[30] = isStorm;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== drop.wave || $[34] !== showWaveInfo) {
    t16 = showWaveInfo && (() => {
      const waveDetails = drop.wave as unknown as {
        chat?: {
          scope?: {
            group?: {
              is_direct_message?: boolean | undefined;
            } | undefined;
          } | undefined;
        } | undefined;
      };
      const isDirectMessage = waveDetails.chat?.scope?.group?.is_direct_message ?? false;
      const waveHref = getWaveRoute({
        waveId: drop.wave.id,
        isDirectMessage,
        isApp: false
      });
      return <Link href={waveHref} onClick={_temp2} className="tw-mt-0.5 tw-text-xs tw-leading-none tw-text-iron-500 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-300">{drop.wave.name}</Link>;
    })();
    $[33] = drop.wave;
    $[34] = showWaveInfo;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t15 || $[37] !== t16) {
    t17 = <div className="tw-flex tw-flex-col tw-items-start">{t15}{t16}</div>;
    $[36] = t15;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== activePartIndex || $[40] !== drop || $[41] !== handleLongPress || $[42] !== hasTouch || $[43] !== onDropContentClick || $[44] !== onQuoteClick) {
    t18 = <div className="tw-mt-2"><WaveDropContent drop={drop} activePartIndex={activePartIndex} setActivePartIndex={setActivePartIndex} onDropContentClick={onDropContentClick} onQuoteClick={onQuoteClick} onLongPress={handleLongPress} setLongPressTriggered={setLongPressTriggered} isCompetitionDrop={true} hasTouch={hasTouch} /></div>;
    $[39] = activePartIndex;
    $[40] = drop;
    $[41] = handleLongPress;
    $[42] = hasTouch;
    $[43] = onDropContentClick;
    $[44] = onQuoteClick;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== t17 || $[47] !== t18) {
    t19 = <div className="tw-flex tw-w-full tw-flex-col">{t17}{t18}</div>;
    $[46] = t17;
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== t12 || $[50] !== t19) {
    t20 = <div className="tw-relative tw-z-10 tw-flex tw-w-full tw-gap-x-3 tw-border-0 tw-bg-transparent tw-text-left">{t12}{t19}</div>;
    $[49] = t12;
    $[50] = t19;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== activePartIndex || $[53] !== drop || $[54] !== handleOnReply || $[55] !== isMobile || $[56] !== showReplyAndQuote) {
    t21 = !isMobile && showReplyAndQuote && <div className="tw-absolute tw-right-0 tw-top-1"><WaveDropActions drop={drop} activePartIndex={activePartIndex} onReply={handleOnReply} /></div>;
    $[52] = activePartIndex;
    $[53] = drop;
    $[54] = handleOnReply;
    $[55] = isMobile;
    $[56] = showReplyAndQuote;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  let t22;
  if ($[58] !== drop) {
    t22 = <WaveWinnerIdentity drop={drop} variant="full" cardVariant="chat" />;
    $[58] = drop;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  let t23;
  if ($[60] !== visibleMetadata) {
    t23 = visibleMetadata.length > 0 && <WaveDropMetadata metadata={visibleMetadata} />;
    $[60] = visibleMetadata;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  let t24;
  if ($[62] !== drop) {
    t24 = !!drop.raters_count && <WaveDropRatings drop={drop} />;
    $[62] = drop;
    $[63] = t24;
  } else {
    t24 = $[63];
  }
  let t25;
  if ($[64] !== drop) {
    t25 = <WaveDropReactions drop={drop} />;
    $[64] = drop;
    $[65] = t25;
  } else {
    t25 = $[65];
  }
  let t26;
  if ($[66] !== t24 || $[67] !== t25) {
    t26 = <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">{t24}{t25}</div>;
    $[66] = t24;
    $[67] = t25;
    $[68] = t26;
  } else {
    t26 = $[68];
  }
  let t27;
  if ($[69] !== t22 || $[70] !== t23 || $[71] !== t26) {
    t27 = <div className="tw-ml-[3.25rem] tw-flex tw-flex-col tw-gap-2">{t22}{t23}{t26}</div>;
    $[69] = t22;
    $[70] = t23;
    $[71] = t26;
    $[72] = t27;
  } else {
    t27 = $[72];
  }
  let t28;
  if ($[73] !== t10 || $[74] !== t11 || $[75] !== t20 || $[76] !== t21 || $[77] !== t27 || $[78] !== t8) {
    t28 = <div className={t8} style={t10}>{t11}{t20}{t21}{t27}</div>;
    $[73] = t10;
    $[74] = t11;
    $[75] = t20;
    $[76] = t21;
    $[77] = t27;
    $[78] = t8;
    $[79] = t28;
  } else {
    t28 = $[79];
  }
  let t29;
  if ($[80] !== drop || $[81] !== handleOnReply || $[82] !== isSlideUp || $[83] !== longPressTriggered || $[84] !== showReplyAndQuote) {
    t29 = <WaveDropMobileMenu drop={drop} isOpen={isSlideUp} longPressTriggered={longPressTriggered} showReplyAndQuote={showReplyAndQuote} setOpen={setIsSlideUp} onReply={handleOnReply} onAddReaction={handleOnAddReaction} />;
    $[80] = drop;
    $[81] = handleOnReply;
    $[82] = isSlideUp;
    $[83] = longPressTriggered;
    $[84] = showReplyAndQuote;
    $[85] = t29;
  } else {
    t29 = $[85];
  }
  let t30;
  if ($[86] !== t28 || $[87] !== t29 || $[88] !== t5) {
    t30 = <div className={t5}>{t28}{t29}</div>;
    $[86] = t28;
    $[87] = t29;
    $[88] = t5;
    $[89] = t30;
  } else {
    t30 = $[89];
  }
  return t30;
};
export default memo(DefaultWinnerDrop);
function _temp(_loc) {
  return "tw-bg-iron-950";
}
function _temp2(e) {
  return e.stopPropagation();
}